import { ethers } from "ethers";
import {
  computeSolanaRecoveryRecordsDigest,
  type SolanaRecoveryRecordInput,
  decodeSolanaCommitmentRecord,
  getSolanaAccounts,
  solanaPoolAddresses,
  solanaPubkeyBase58,
  type SolanaInstruction,
} from "@stealthos/sdk";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../../joinsplit/types";
import { computeNullifier } from "../../joinsplit/keys";
import { flatFieldHex } from "../notes";
import {
  planDepositMerge,
  type DepositMergeCapability,
  type DepositMergeCandidate,
  type DepositMergeScope,
} from "../depositMergePlan";
import {
  createDepositMergeProverFromEnv,
  type DepositMergeProofResponse,
  type DepositMergeProver,
} from "../depositMergeProver";
import { inputWitness } from "../witness";
import type { SolanaNetworkConfig } from "../../config/solana";
import { solanaPoolAccountExists } from "./accounts";
import { assertNotAborted, fail } from "./common";
import { scanSolanaTree } from "./leafDiscovery";
import {
  createSolanaFlatNote,
  isSolanaFlatNote,
  solanaNoteMint,
  solanaTokenProgramForNote,
  solanaTreeKey,
} from "./notes";
import { getSolanaTransaction, type SolanaRpc } from "./rpc";
import { synchronizeSolanaNotes } from "./sync";
import type { SolanaLeafCache } from "./leafCache";
import { solanaJournalReservations, type SolanaJournalScope } from "./journal";
import {
  prepareSolanaNoteRecovery,
  type SolanaRecoveryPublication,
} from "./onchainRecovery";
import { formatInboxPublicKeyHex } from "../../utils/inbox-crypto";

const FAMILY = "deposit-merge-v1" as const;
const MAX_DEADLINE_SLOTS = 1_200n;

interface SolanaDepositMergedEvent {
  name: "DepositMerged";
  nullifier: string;
  commitment: string;
  treeIndex: number;
  leafIndex: number;
  mint: string;
  netCredit: bigint;
  merkleRoot: string;
  deadlineSlot: bigint;
  nonce: string;
}

interface DepositMergeSdkSurface {
  buildSolanaDepositMerge?: (params: {
    recoveryRecords?: SolanaRecoveryRecordInput[];
    cluster: string;
    programId: string;
    depositor: string;
    mint: string | null;
    tokenProgram?: string;
    currentTreeIndex: number;
    merkleRoot: `0x${string}`;
    nullifier: `0x${string}`;
    commitment: `0x${string}`;
    amount: bigint;
    deadlineSlot: bigint;
    nonce: `0x${string}`;
    proof: DepositMergeProofResponse;
  }) => Promise<{
    instruction: SolanaInstruction;
    tokenHash: string;
    treeIndex: number;
    publicInputs: readonly string[];
  }>;
  extractSolanaPoolEvents?: (
    transaction: unknown,
    programId: string,
  ) => Array<{ event: { name: string } & Record<string, unknown> }>;
  solanaDepositMergePublicInputs?: (params: {
    merkleRoot: `0x${string}`;
    nullifier: `0x${string}`;
    outputCommitment: `0x${string}`;
    tokenHash: `0x${string}`;
    funding: {
      recoveryDigest?: `0x${string}`;
      cluster: string;
      programId: string;
      treeIndex: number;
      mint: string | null;
      payer: string;
      grossAmount: bigint;
      netCredit: bigint;
      deadlineSlot: bigint;
      nonce: `0x${string}`;
    };
  }) => Promise<readonly string[]>;
}

export interface SolanaDepositMergePrepared {
  recoveryPublication?: SolanaRecoveryPublication;
  note: JoinSplitNote;
  input: StoredJoinSplitNote;
  sourceNullifier: `0x${string}`;
  merkleRoot: `0x${string}`;
  tokenHash: `0x${string}`;
  instruction: SolanaInstruction;
  deadlineSlot: bigint;
  nonce: `0x${string}`;
  provingTimeMs: number;
}

export interface PrepareSolanaDepositMergeParams {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  payer: string;
  mint: string | null;
  tokenProgram: string;
  netCredit: bigint;
  senderMpk: bigint;
  recipientMpk: bigint;
  spendingKeys: SpendingKeys;
  notes: StoredJoinSplitNote[];
  cache?: SolanaLeafCache;
  journalScope: SolanaJournalScope;
  currentTreeIndex: number;
  currentSlot: number;
  tokenHash: `0x${string}`;
  privateHistory?: JoinSplitNote["privateHistory"];
  signal?: AbortSignal;
  onProgress?: (phase: "loading" | "proving" | "checking") => void;
  /** Tests can provide a deterministic prover without loading release artifacts. */
  createProver?: () => DepositMergeProver | null;
  /** Tests can inject the feature SDK without replacing the pinned package. */
  sdkSurface?: DepositMergeSdkSurface;
}

function mergeEnabled(network: SolanaNetworkConfig): boolean {
  return network.depositMergeEnabled === true;
}

async function loadSdkSurface(): Promise<DepositMergeSdkSurface | null> {
  const imported =
    (await import("@stealthos/sdk")) as unknown as DepositMergeSdkSurface;
  if (
    typeof imported.buildSolanaDepositMerge !== "function" ||
    typeof imported.solanaDepositMergePublicInputs !== "function"
  ) {
    return null;
  }
  return imported;
}

function randomNonce(): `0x${string}` {
  for (let attempt = 0; attempt < 32; attempt += 1) {
    const nonce = ethers.hexlify(ethers.randomBytes(32)).toLowerCase();
    if (BigInt(nonce) !== 0n) return nonce as `0x${string}`;
  }
  return fail("secure randomness did not produce a deposit merge nonce");
}

function canonicalProof(
  response: DepositMergeProofResponse,
  expected: readonly string[],
): DepositMergeProofResponse {
  if (
    response?.family !== FAMILY ||
    response.locallyVerified !== true ||
    !/^0x[0-9a-fA-F]{512}$/.test(response.proof) ||
    !Array.isArray(response.publicInputs) ||
    response.publicInputs.length !== expected.length
  ) {
    fail("browser returned an invalid DepositMerge proof");
  }
  response.publicInputs.forEach((value, index) => {
    if (
      typeof value !== "string" ||
      value.toLowerCase() !== expected[index].toLowerCase()
    ) {
      fail("DepositMerge proof does not match the intended Solana funding");
    }
  });
  return {
    family: FAMILY,
    proof: response.proof.toLowerCase(),
    publicInputs: response.publicInputs.map((value) => value.toLowerCase()),
    locallyVerified: true,
  };
}

export async function prepareSolanaDepositMerge(
  params: PrepareSolanaDepositMergeParams,
): Promise<SolanaDepositMergePrepared | null> {
  assertNotAborted(params.signal, "before deposit merge preparation");
  if (params.senderMpk !== params.recipientMpk) return null;
  const existing = params.notes.filter(
    ({ note, spent }) =>
      !spent &&
      isSolanaFlatNote(note, params.network) &&
      solanaNoteMint(note) === params.mint,
  );
  if (existing.length === 0) return null;
  // Once a self deposit has an existing balance, every inability to merge is
  // visible. Falling through would silently fragment that balance again.
  if (!mergeEnabled(params.network))
    fail(
      "atomic top-up is unavailable for this network; deposit stopped before signing",
    );
  if (params.spendingKeys.mpk !== params.senderMpk)
    fail("atomic top-up owner does not match the connected private wallet");
  if (
    !Number.isSafeInteger(params.currentTreeIndex) ||
    params.currentTreeIndex < 0 ||
    !Number.isSafeInteger(params.currentSlot) ||
    params.currentSlot < 0 ||
    params.netCredit <= 0n ||
    params.netCredit >= 1n << 64n
  ) {
    fail(
      "atomic top-up has invalid funding or pool state; refresh recovery before retrying",
    );
  }

  const sdk = params.sdkSurface ?? (await loadSdkSurface());
  if (!sdk?.buildSolanaDepositMerge || !sdk.solanaDepositMergePublicInputs) {
    fail("atomic top-up SDK is unavailable; reload the app before retrying");
  }
  const prover =
    params.createProver?.() ?? (await createDepositMergeProverFromEnv());
  if (!prover)
    fail("atomic top-up prover is unavailable; deposit stopped before signing");

  params.onProgress?.("loading");
  const sync = await synchronizeSolanaNotes({
    rpc: params.rpc,
    network: params.network,
    spendingKeys: params.spendingKeys,
    notes: params.notes,
    cache: params.cache,
    signal: params.signal,
    maxTreeScans: params.network.maxTreeScans,
  });
  if (sync.currentTreeIndex !== params.currentTreeIndex) {
    fail(
      "pool tree changed during atomic top-up preparation; refresh recovery before retrying",
    );
  }

  const addresses = solanaPoolAddresses(params.network.programId);
  const scope: DepositMergeScope = {
    network: params.network.key,
    pool: addresses.programId,
    tokenHash: BigInt(params.tokenHash),
    treeIndex: params.currentTreeIndex,
  };
  const capability: DepositMergeCapability = {
    family: FAMILY,
    network: scope.network,
    pool: scope.pool,
    enabled: true,
    feeMode: "none",
    feeAdapterReady: true,
    recoveryReady: true,
  };
  const candidates: DepositMergeCandidate[] = [];
  const reservations = await solanaJournalReservations(params.journalScope);
  for (const stored of sync.validated.values()) {
    if (
      stored.note.treeIndex !== params.currentTreeIndex ||
      flatFieldHex(stored.note.tokenHash) !== params.tokenHash.toLowerCase()
    ) {
      continue;
    }
    try {
      if (solanaNoteMint(stored.note) !== params.mint) continue;
    } catch {
      continue;
    }
    candidates.push({
      stored,
      scope,
      confirmed: stored.note.leafIndex >= 0,
      reserved: reservations.commitments.has(
        stored.note.commitment.toLowerCase(),
      ),
    });
  }
  const plan = planDepositMerge({
    scope,
    senderMpk: params.senderMpk,
    recipientMpk: params.recipientMpk,
    netCredit: params.netCredit,
    candidates,
    capability,
  });
  if (plan.mode !== "merge") {
    fail(
      "existing private balance has no finalized, unreserved note in the current pool tree; refresh recovery or migrate older notes before retrying the deposit",
    );
  }

  const scan = await scanSolanaTree({
    rpc: params.rpc,
    network: params.network,
    treeIndex: params.currentTreeIndex,
    signal: params.signal,
    cache: params.cache,
  });
  const path = scan.tree.path(plan.input.note.leafIndex);
  const witness = inputWitness(plan.input.note, path, params.spendingKeys);
  const sourceNullifier = (
    await computeNullifier(
      params.spendingKeys.nullifyingKey,
      plan.input.note.tokenHash,
      plan.input.note.leafIndex,
    )
  ).toLowerCase() as `0x${string}`;
  const { accounts: nullifierAccounts } = await getSolanaAccounts(
    params.rpc,
    [addresses.nullifier(sourceNullifier)],
    "finalized",
  );
  if (
    solanaPoolAccountExists(
      nullifierAccounts[0],
      params.network.programId,
      "nullifier",
    )
  ) {
    fail(
      "the selected private note was already spent; refresh recovery before retrying the deposit",
    );
  }

  const note: JoinSplitNote = {
    ...(await createSolanaFlatNote({
      recipientMpk: params.spendingKeys.mpk,
      network: params.network,
      treeIndex: params.currentTreeIndex,
      mint: params.mint,
      tokenProgram: params.tokenProgram,
      tokenHash: params.tokenHash,
      value: plan.outputValue,
    })),
    ...(params.privateHistory
      ? { privateHistory: { ...params.privateHistory } }
      : {}),
  };
  if (
    solanaTokenProgramForNote(note) !== params.tokenProgram &&
    params.mint !== null
  ) {
    fail("replacement note token program differs from the deposit");
  }

  const deadlineSlot = BigInt(params.currentSlot) + MAX_DEADLINE_SLOTS;
  const nonce = randomNonce();
  const recoveryPublication = params.network.onchainRecoveryEnabled
    ? await prepareSolanaNoteRecovery(
        params.network,
        note,
        formatInboxPublicKeyHex(params.spendingKeys.spendingKey),
      )
    : undefined;
  const recoveryRecords: SolanaRecoveryRecordInput[] | undefined =
    recoveryPublication
      ? [
          {
            kind: 1,
            locator: recoveryPublication.locator,
            payload: ethers.hexlify(
              recoveryPublication.payload,
            ) as `0x${string}`,
          },
        ]
      : undefined;
  const funding = {
    recoveryDigest: recoveryRecords
      ? computeSolanaRecoveryRecordsDigest({
          cluster: params.network.cluster,
          programId: params.network.programId,
          commitments: [note.commitment as `0x${string}`],
          records: recoveryRecords,
        })
      : undefined,
    cluster: params.network.cluster,
    programId: addresses.programId,
    treeIndex: params.currentTreeIndex,
    mint: params.mint,
    payer: params.payer,
    grossAmount: params.netCredit,
    netCredit: params.netCredit,
    deadlineSlot,
    nonce,
  };
  const expected = await sdk.solanaDepositMergePublicInputs({
    merkleRoot: scan.root as `0x${string}`,
    nullifier: sourceNullifier,
    outputCommitment: note.commitment as `0x${string}`,
    tokenHash: params.tokenHash,
    funding,
  });

  assertNotAborted(params.signal, "before deposit merge proving");
  params.onProgress?.("proving");
  const provingStarted = Date.now();
  const response = await prover.prove(
    {
      merkle_root: BigInt(scan.root).toString(),
      input: {
        npk: BigInt(witness.npk).toString(),
        token_hash: BigInt(witness.token_hash).toString(),
        value: String(witness.value),
        random: BigInt(witness.random).toString(),
        leaf_index: String(witness.leaf_index),
        nullifying_key: BigInt(witness.nullifying_key).toString(),
        pub_key_x: BigInt(witness.pub_key_x).toString(),
        pub_key_y: BigInt(witness.pub_key_y).toString(),
        path_elements: witness.path_elements.map((value) =>
          BigInt(value).toString(),
        ),
        path_indices: [...witness.path_indices],
      },
      spend_secret_scalar: params.spendingKeys.spendSecretScalar.toString(),
      output_random: note.random.toString(),
      output_value: note.value.toString(),
      net_credit: params.netCredit.toString(),
      funding_context: BigInt(expected[5]).toString(),
    },
    params.signal,
  );
  const proof = canonicalProof(response, expected);
  assertNotAborted(params.signal, "after deposit merge proving");
  params.onProgress?.("checking");

  const built = await sdk.buildSolanaDepositMerge({
    recoveryRecords,
    cluster: params.network.cluster,
    programId: addresses.programId,
    depositor: params.payer,
    mint: params.mint,
    tokenProgram: params.tokenProgram,
    currentTreeIndex: params.currentTreeIndex,
    merkleRoot: scan.root as `0x${string}`,
    nullifier: sourceNullifier,
    commitment: note.commitment as `0x${string}`,
    amount: params.netCredit,
    deadlineSlot,
    nonce,
    proof,
  });
  if (
    built.treeIndex !== params.currentTreeIndex ||
    built.tokenHash.toLowerCase() !== params.tokenHash.toLowerCase()
  ) {
    fail("DepositMerge SDK domain differs from the finalized pool state");
  }

  return {
    recoveryPublication,
    note,
    input: plan.input,
    sourceNullifier,
    merkleRoot: scan.root as `0x${string}`,
    tokenHash: params.tokenHash,
    instruction: built.instruction,
    deadlineSlot,
    nonce,
    provingTimeMs: Date.now() - provingStarted,
  };
}

export async function finalizeSolanaDepositMerge(params: {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  signature: string;
  prepared: Pick<
    SolanaDepositMergePrepared,
    "note" | "sourceNullifier" | "deadlineSlot" | "nonce"
  >;
  mint: string | null;
  netCredit: bigint;
  sdkSurface?: DepositMergeSdkSurface;
}): Promise<{ slot: number; note: JoinSplitNote }> {
  const sdk = params.sdkSurface ?? (await loadSdkSurface());
  if (!sdk?.extractSolanaPoolEvents) {
    fail("DepositMerge event decoder is unavailable in this SDK build");
  }
  const transaction = await getSolanaTransaction(
    params.rpc,
    params.signature,
    "finalized",
  );
  if (!transaction || transaction.meta?.err) {
    fail("finalized DepositMerge transaction is unavailable or failed");
  }
  const events = sdk
    .extractSolanaPoolEvents(transaction, params.network.programId)
    .map((entry) => entry.event)
    .filter(
      (event) => event.name === "DepositMerged",
    ) as unknown as SolanaDepositMergedEvent[];
  const event = events.find(
    (candidate) =>
      String(candidate.nullifier).toLowerCase() ===
        params.prepared.sourceNullifier.toLowerCase() &&
      String(candidate.commitment).toLowerCase() ===
        params.prepared.note.commitment.toLowerCase(),
  );
  if (!event) {
    fail("transaction carries no matching DepositMerged event");
  }
  const expectedMint = solanaPubkeyBase58(
    params.mint ?? "11111111111111111111111111111111",
    "deposit merge mint",
  );
  if (
    event.treeIndex !== params.prepared.note.treeIndex ||
    !Number.isSafeInteger(event.leafIndex) ||
    event.leafIndex < 0 ||
    event.mint !== expectedMint ||
    event.netCredit !== params.netCredit ||
    event.deadlineSlot !== params.prepared.deadlineSlot ||
    event.nonce.toLowerCase() !== params.prepared.nonce.toLowerCase()
  ) {
    fail("DepositMerged event differs from the prepared deposit");
  }

  const addresses = solanaPoolAddresses(params.network.programId);
  const { accounts } = await getSolanaAccounts(
    params.rpc,
    [
      addresses.nullifier(params.prepared.sourceNullifier),
      addresses.commitmentRecord(
        params.prepared.note.commitment as `0x${string}`,
      ),
    ],
    "finalized",
  );
  if (
    !solanaPoolAccountExists(accounts[0], params.network.programId, "nullifier")
  ) {
    fail("DepositMerge nullifier was not consumed");
  }
  const commitmentAccount = accounts[1];
  if (
    !commitmentAccount ||
    commitmentAccount.owner !== params.network.programId
  ) {
    fail("DepositMerge replacement commitment record is missing");
  }
  const record = decodeSolanaCommitmentRecord(commitmentAccount.data);
  if (
    record.treeIndex !== event.treeIndex ||
    record.leafIndex !== event.leafIndex
  ) {
    fail("DepositMerge event and commitment record disagree");
  }

  return {
    slot: transaction.slot,
    note: {
      ...params.prepared.note,
      leafIndex: event.leafIndex,
      treeIndex: event.treeIndex,
      treeKey: solanaTreeKey(
        params.network.key,
        params.network.programId,
        event.treeIndex,
      ),
      merkleRoot: event.merkleRoot,
    },
  };
}
